import { useState } from "react"

import LinkPills from "../../links/pages/LinkPills"
import type { LinkProp } from "../../links/pages/LinkPills"

// FR-11, AC-7: exactly the eight per-stay values Schedule::DaysController
// sends -- no `id`, no `notes` (FR-7). `checkOutDate`/`checkOutTime24` are
// the machine keys `isStayPast` below compares; `checkInLabel`/
// `checkOutLabel` are the whole, ready-to-render strings the model
// composed server-side -- this component formats neither.
// t018 `impl-stay-pills` (FR-19, FR-26..FR-28): `links` is the stay's own
// labelled links, in `position` order -- rendered only inside the
// drawer (below), never on the folded row.
export interface StayProps {
  name: string
  address: string
  mapsUrl: string
  checkInLabel: string
  checkOutLabel: string
  checkOutDate: string
  checkOutTime24: string
  links: LinkProp[]
}

// FR-13.2: chrome, not per-stay data -- the folded row's «Карта» pill
// label and the unfolded drawer's boundary-label separator, both read
// once by the day page and handed to every row.
export interface StayLabels {
  map: string
  separator: string
}

// FR-17: the one rule, "past is grey" -- a stay is past exactly when its
// check-out date is before today, or equal to today with its check-out
// time before now. A pure four-string function, not a hook: Day.tsx
// supplies `today`/`now` from its own single `useTripClock` call, so
// this module adds no second clock read anywhere (NFR-3-style, plan
// ruling). Plain string comparison, no parsing, no date library -- the
// `isItemPast` shape this copies.
export function isStayPast(
  checkOutDate: string,
  checkOutTime24: string,
  today: string,
  now: string,
): boolean {
  if (checkOutDate < today) return true
  if (checkOutDate === today) return checkOutTime24 < now

  return false
}

const ROW_CLASSES = "flex flex-col rounded-[10px] py-2 pl-3 pr-2.5"
const EXPANDED_ROW_CLASSES = "flex flex-col gap-1.5 rounded-[10px] py-2 pl-3 pr-2.5"

const PILL_CLASSES =
  "inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[13px] " +
  "font-medium leading-[18px] text-accent"
const PAST_PILL_CLASSES =
  "inline-flex shrink-0 items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-[13px] " +
  "font-medium leading-[18px] text-gray-500"

interface StayRowProps {
  stay: StayProps
  isPast: boolean
  labels: StayLabels
}

// docs/design/stays.html Block 1, implemented verbatim. Folded by default,
// always (FR-16) -- expanded/folded state lives in this component's own
// `useState`, never persisted, so every row starts folded again on
// mount (Day.tsx remounts the whole stays container on navigation, see
// its own comment). E-1 (owner ruling, superseding FR-16's original
// wording): the drawer's address is plain text, never an anchor -- the
// «Карта» pill, a sibling <a> beside the toggle button and visible while
// folded, is this row's one and only link to `mapsUrl`.
//
// The past (grey) treatment applies to exactly five elements -- the
// container, the house icon, the name, the chevron and the pill -- the
// pill staying a working link either way (an old stay's address may
// still be needed). The drawer's own text keeps its live ink regardless
// of `isPast`: the grey-and-expanded combination is undrawn in the
// frozen design.
//
// t018 `impl-stay-pills` (FR-26..FR-28): the stay's own links render
// inside the drawer, under the boundary-label line, as the same one
// wrapping pill row `Day.tsx`'s `TimelineRow` renders (`../../links/
// pages/LinkPills`, this file's own `stays` -> `links` TSX import,
// FR-37) -- `bg-white` for a current stay, the same past-grey `LinkPills`
// already names for a past item. `LinkPills` itself renders nothing at
// all for zero links (FR-28), so a stay without links leaves the drawer
// exactly as t016 left it, with no extra guard needed here. The folded
// row and its derived «Карта» pill above are untouched either way
// (FR-26).
export default function StayRow({ stay, isPast, labels }: StayRowProps) {
  const [open, setOpen] = useState(false)

  return (
    <div
      className={`${open ? EXPANDED_ROW_CLASSES : ROW_CLASSES} ${
        isPast ? "bg-gray-50" : "bg-accent/5"
      }`}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="flex min-w-0 grow items-center gap-2 text-left"
        >
          <svg
            aria-hidden="true"
            className={`shrink-0 ${isPast ? "text-gray-400" : "text-accent"}`}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M2 7.5L8 2.5l6 5M4 6.5V13.5h8V6.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span
            className={`truncate text-sm font-semibold leading-5 ${
              isPast ? "text-gray-500" : "text-gray-900"
            }`}
          >
            {stay.name}
          </span>
          <svg
            aria-hidden="true"
            className={`shrink-0 ${isPast ? "text-gray-300" : "text-gray-400"}`}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              d={open ? "M3 7.5l3-3 3 3" : "M3 4.5l3 3 3-3"}
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {/* E-1 (docs/design/stays.html:78-91, docs/framing/stays.md rulings
            8-9): the row's only maps link -- a sibling <a>, never nested
            inside the toggle button above, visible in the folded state so
            the map is one tap away regardless of the drawer. `noreferrer`
            is one attribute stricter than the design contract's literal
            `rel="noopener"` -- the `TimelineRow` pill's own precedent
            (R-6), required by `react/jsx-no-target-blank` regardless. */}
        <a
          href={stay.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={isPast ? PAST_PILL_CLASSES : PILL_CLASSES}
        >
          {labels.map}
          <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M4 2.5h5.5V8M9.5 2.5L2.5 9.5"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      </div>
      {open && (
        <div className="flex flex-col gap-0.5 pb-0.5 pl-6">
          {/* E-1: plain text, never an anchor -- the pill above is the
              row's only link to mapsUrl. */}
          <p className="text-[13px] leading-[18px] text-gray-700">{stay.address}</p>
          <p className="text-xs leading-4 text-gray-500 tabular-nums">
            {stay.checkInLabel}
            {labels.separator}
            {stay.checkOutLabel}
          </p>
          {/* t018 `impl-stay-pills` (FR-27, FR-28): the stay's own links,
              under the boundary line -- bg-white current, grey past,
              nothing at all with zero links. */}
          <LinkPills
            links={stay.links}
            variant={isPast ? "grey" : "white"}
            marginClassName="mt-0.5"
          />
        </div>
      )}
    </div>
  )
}
